import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { withTranslation } from 'react-i18next';
import { InternalGrid, Box } from '@paygreen/paygreen-ui';
import { useQueryState } from 'react-router-use-location-state';
import { useAPIError, useMode } from '../../../context';
import {
    formatDateApi,
    IsBigDesktop,
    IsDesktop,
    IsMobile,
} from '../../../utils/tools';
import { startOfMonth, endOfMonth } from '../../../utils/constants';
import {
    H1Title,
    ErrorBoundary,
    ModalIcon,
    ToggleMode,
} from '../../../components';
import { getAllFootprints } from '../service';
import CarbonExplanationModal from './shared/CarbonExplanationModal';
import OffsetsTable from './OffsetsTable';
import FilterTable from './FilterTable';

const Offsets = ({ t }) => {
    const { addError } = useAPIError();
    const { mode } = useMode();

    const fetchIdRef = useRef(0);

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    const [isLoading, setIsLoading] = useState(true);
    const [dataOffsets, setOffsets] = useState([]);
    const [pageCount, setPageCount] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [startDate, setStartDate] = useQueryState('startDate', startOfMonth);
    const [endDate, setEndDate] = useQueryState('endDate', endOfMonth);
    const [activePageReset, setActivePageReset] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');

    // to reset pageIndex and filters when mode changes
    const handleModeChange = () => {
        setActivePageReset(true);
        setStatusFilter('');
    };

    const fetchData = useCallback(
        ({ pageSize, pageIndex, statusFilter, startDate, endDate }) => {
            const fetchId = ++fetchIdRef.current;
            if (fetchId === fetchIdRef.current) {
                isMounted.current && setIsLoading(true);

                const params = {
                    limit: pageSize,
                    page: pageIndex + 1,
                    status: statusFilter,
                    begin: startDate && formatDateApi(startDate),
                    end: endDate && formatDateApi(endDate),
                };

                getAllFootprints(params, mode)
                    .then((res) => {
                        if (isMounted.current) {
                            setOffsets(res.data._embedded.footprint);
                            setPageCount(res.data.page_count);
                            setTotalItems(res.data.total_items);
                        }
                    })
                    .catch((error) => {
                        if (error?.response?.data.status === (404 || 409)) {
                            isMounted.current && setOffsets([]);
                        } else {
                            addError(
                                error?.response?.data.detail,
                                error?.response?.data.status,
                            );
                        }
                    })
                    .finally(() => {
                        isMounted.current && setIsLoading(false);
                    });
            }
        },
        [addError, mode],
    );

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
        };
    }, []);

    return (
        <>
            <Helmet>
                <title>
                    {t('offsets.metaTitle')} - {t('main.metaTitle')}
                </title>
            </Helmet>

            <H1Title
                marginTop={IsDesktop() ? 'none' : 'xs'}
                title={t('offsets.title')}
                infoIcon={
                    <ModalIcon
                        title={t('dashboard.equivalence.modal.title')}
                        icon="QuestionBoldIcon"
                        iconSize="sm"
                    >
                        <CarbonExplanationModal />
                    </ModalIcon>
                }
            >
                <ToggleMode externalChange={handleModeChange} />
            </H1Title>

            <InternalGrid
                displayType="grid"
                gridGap="md"
                gridTemplateColumns="1fr"
                blockPadding="none"
                justifyItems="stretch"
                hasStaticWidth={IsBigDesktop() ? false : true}
            >
                <Box
                    marginBottom={IsDesktop() ? 'lg' : 'md'}
                    style={{
                        width: IsMobile() ? 'fit-content' : 'max-content',
                    }}
                >
                    <FilterTable
                        setEndDate={setEndDate}
                        setStartDate={setStartDate}
                        startDate={startDate}
                        endDate={endDate}
                    />
                </Box>

                <ErrorBoundary>
                    <OffsetsTable
                        data={dataOffsets}
                        startDate={startDate}
                        endDate={endDate}
                        fetchData={fetchData}
                        loading={isLoading}
                        pageCount={pageCount}
                        totalItems={totalItems}
                        setActivePageReset={setActivePageReset}
                        activePageReset={activePageReset}
                        statusFilter={statusFilter}
                        setStatusFilter={setStatusFilter}
                    />
                </ErrorBoundary>
            </InternalGrid>
        </>
    );
};

export default withTranslation('common')(Offsets);
